import React, {useCallback, useState} from 'react';
import {
  ImageBackground,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View
} from 'react-native';
import i18n from 'i18n-js';
import backgroundImage from '../../../../assets/background.png';
import {colors} from '../../../style/rulesEngine';
import {fontFamily} from '../../../helpers/fontFamily';
import {
  accentColor,
  groupCountPillText,
  mobileInputFontSize,
  textColor,
  textInputBg,
  textInputRadius,
  textMutedColor,
  toggleOffColor,
  touchButtonHeight,
  touchTargetMin
} from '../../../style/adminTools';
import {
  appBarHeight,
  mobileGutter,
  mobileTitleSize,
  stickyBarBg,
  touchActionBarButtonHeight
} from '../../../style/adminTenantGrid';
import {
  EmptySearchPanel,
  FailureBanner,
  FailurePanel,
  NoResultsPanel
} from './GridStateBanner';
import PhoneFiltersSheet from './PhoneFiltersSheet';
import ShimmerRows from './ShimmerRows';
import TenantPhoneCard from './TenantPhoneCard';
import {activeFilterCount} from './phoneSortOptions';
import usePhoneTenantPage from './usePhoneTenantPage';

const t = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

const ACTION_NAMES = ['duplicate', 'delete', 'restore'];

const SKELETON_CARDS = 4;

const SkeletonCards = () => (
  <View testID="tenantSkeleton">
    {Array.from({length: SKELETON_CARDS}, (_, index) => (
      <View key={index} style={{marginBottom: 10}}>
        <ShimmerRows
          testID="tenantSkeletonCard"
          count={1}
          rowHeight={150}
          rowRadius={10}
        />
      </View>
    ))}
  </View>
);

const AppBar = ({title, subline, onOpenMenu}) => (
  <View
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      height: appBarHeight,
      paddingHorizontal: mobileGutter
    }}
  >
    <TouchableOpacity
      testID="adminPlaceholderMenuButton"
      accessibilityRole="button"
      accessibilityLabel="Open menu"
      onPress={onOpenMenu}
      style={{
        width: touchTargetMin,
        height: touchTargetMin,
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <i className="icon-menu" style={{color: colors.text, fontSize: 24}} />
    </TouchableOpacity>
    <View style={{marginLeft: 6, flex: 1}}>
      <Text
        accessibilityRole="header"
        style={{
          fontFamily: fontFamily.font700,
          fontSize: mobileTitleSize,
          color: textColor
        }}
      >
        {title}
      </Text>
      <Text
        testID="tenantSubline"
        numberOfLines={1}
        style={{
          fontFamily: fontFamily.font500,
          fontSize: 12,
          color: textMutedColor
        }}
      >
        {subline}
      </Text>
    </View>
  </View>
);

const SelectBar = ({count, onClose, onSelectAll}) => (
  <View
    testID="phoneSelectBar"
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      height: appBarHeight,
      paddingHorizontal: mobileGutter,
      backgroundColor: stickyBarBg
    }}
  >
    <Pressable
      testID="phoneSelectClose"
      accessibilityRole="button"
      accessibilityLabel={t('closeSelection')}
      onPress={onClose}
      style={{
        width: touchTargetMin,
        height: touchTargetMin,
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Text style={{color: textColor, fontSize: 22}}>{'×'}</Text>
    </Pressable>
    <Text
      testID="phoneSelectedCount"
      style={{
        flex: 1,
        marginLeft: 6,
        fontFamily: fontFamily.font700,
        fontSize: 16,
        color: textColor
      }}
    >
      {t('selectedCount', {count})}
    </Text>
    <Pressable
      testID="phoneSelectAll"
      accessibilityRole="button"
      onPress={onSelectAll}
      style={{
        minHeight: touchTargetMin,
        paddingHorizontal: 8,
        justifyContent: 'center'
      }}
    >
      <Text
        style={{
          fontFamily: fontFamily.font600,
          fontSize: 14,
          color: accentColor
        }}
      >
        {t('selectAll')}
      </Text>
    </Pressable>
  </View>
);

const StickyBar = ({
  searchValue,
  onSearchChange,
  onClearSearch,
  filterCount,
  onOpenFilters,
  summary,
  canSelect,
  onSelect
}) => (
  <View
    testID="phoneStickyBar"
    style={{
      paddingHorizontal: mobileGutter,
      paddingBottom: 4,
      backgroundColor: stickyBarBg
    }}
  >
    <View style={{flexDirection: 'row', alignItems: 'center', paddingTop: 8}}>
      <View
        testID="phoneSearchBox"
        style={{
          flex: 1,
          minWidth: 0,
          flexDirection: 'row',
          alignItems: 'center',
          height: touchButtonHeight,
          paddingLeft: 12,
          backgroundColor: textInputBg,
          borderRadius: textInputRadius
        }}
      >
        <TextInput
          testID="tenantSearchInput"
          value={searchValue}
          onChangeText={onSearchChange}
          placeholder={t('searchPlaceholder')}
          placeholderTextColor={textMutedColor}
          style={{
            flex: 1,
            minWidth: 0,
            height: touchButtonHeight,
            outlineStyle: 'none',
            fontFamily: fontFamily.font500,
            fontSize: mobileInputFontSize,
            color: textColor
          }}
        />
        {searchValue ? (
          <Pressable
            testID="clearSearchButton"
            accessibilityLabel={t('clearSearch')}
            onPress={onClearSearch}
            style={{
              width: touchTargetMin,
              height: touchTargetMin,
              flexShrink: 0,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Text style={{color: textMutedColor, fontSize: 20}}>{'×'}</Text>
          </Pressable>
        ) : null}
      </View>
      <Pressable
        testID="phoneFiltersButton"
        accessibilityRole="button"
        accessibilityLabel={t('filtersTitle')}
        onPress={onOpenFilters}
        style={{
          width: touchButtonHeight,
          height: touchButtonHeight,
          flexShrink: 0,
          marginLeft: 8,
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: textInputRadius,
          backgroundColor: textInputBg
        }}
      >
        <Text style={{color: textColor, fontSize: 20}}>{'≡'}</Text>
        {filterCount > 0 ? (
          <View
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: accentColor
            }}
          >
            <Text testID="phoneFiltersBadge" style={groupCountPillText}>
              {String(filterCount)}
            </Text>
          </View>
        ) : null}
      </Pressable>
    </View>
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Text
        testID="phoneSummary"
        style={{
          flex: 1,
          fontFamily: fontFamily.font500,
          fontSize: 13,
          color: textMutedColor
        }}
      >
        {summary}
      </Text>
      {canSelect ? (
        <Pressable
          testID="phoneSelectLink"
          accessibilityRole="button"
          onPress={onSelect}
          style={{
            minWidth: touchTargetMin,
            minHeight: touchTargetMin,
            justifyContent: 'center',
            alignItems: 'flex-end'
          }}
        >
          <Text
            style={{
              fontFamily: fontFamily.font600,
              fontSize: 14,
              color: accentColor
            }}
          >
            {t('select')}
          </Text>
        </Pressable>
      ) : (
        <View style={{minHeight: touchTargetMin}} />
      )}
    </View>
  </View>
);

const PagerButton = ({label, disabled, onPress, testID}) => (
  <Pressable
    testID={testID}
    accessibilityRole="button"
    accessibilityState={{disabled}}
    disabled={disabled}
    onPress={onPress}
    style={{
      height: touchButtonHeight,
      minWidth: 96,
      marginLeft: 8,
      paddingHorizontal: 16,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 7,
      backgroundColor: textInputBg,
      opacity: disabled ? 0.4 : 1
    }}
  >
    <Text
      style={{fontFamily: fontFamily.font600, fontSize: 14, color: textColor}}
    >
      {label}
    </Text>
  </Pressable>
);

const PhonePager = ({skip, limit, total, onPrevious, onNext}) => (
  <View
    testID="tenantPager"
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 8
    }}
  >
    <Text
      testID="tenantPagerRange"
      style={{
        flex: 1,
        fontFamily: fontFamily.font500,
        fontSize: 13,
        color: textMutedColor
      }}
    >
      {total > 0
        ? t('pagerRange', {
            from: skip + 1,
            to: Math.min(skip + limit, total),
            total
          })
        : ''}
    </Text>
    <PagerButton
      testID="pagerPrev"
      label={t('previous')}
      disabled={skip <= 0}
      onPress={onPrevious}
    />
    <PagerButton
      testID="pagerNext"
      label={t('next')}
      disabled={skip + limit >= total}
      onPress={onNext}
    />
  </View>
);

const ActionBar = ({actions, reason, onDisabledPress}) => (
  <View
    testID="phoneActionBar"
    style={{
      backgroundColor: stickyBarBg,
      borderTopWidth: 1,
      borderTopColor: toggleOffColor
    }}
  >
    {reason ? (
      <Text
        testID="phoneActionReason"
        accessibilityRole="alert"
        style={{
          padding: 10,
          fontFamily: fontFamily.font500,
          fontSize: 13,
          color: textColor
        }}
      >
        {reason}
      </Text>
    ) : null}
    <View style={{flexDirection: 'row', padding: 8}}>
      {ACTION_NAMES.map(name => {
        const {enabled, onPress, reason: why} = actions[name];
        const danger = name === 'delete';
        return (
          <Pressable
            key={name}
            testID={`phoneAction-${name}`}
            accessibilityRole="button"
            accessibilityState={{disabled: !enabled}}
            onPress={() => (enabled ? onPress() : onDisabledPress(why))}
            style={{
              flex: 1,
              height: touchActionBarButtonHeight,
              marginHorizontal: 4,
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 8,
              borderWidth: 2,
              borderColor: enabled && danger ? colors.danger : toggleOffColor,
              opacity: enabled ? 1 : 0.5
            }}
          >
            <Text
              style={{
                fontFamily: fontFamily.font600,
                fontSize: 14,
                color: enabled && danger ? colors.danger : textColor
              }}
            >
              {t(name)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  </View>
);

const TenantPhone = ({
  onOpenMenu,
  onOpenTenant,
  search,
  filters,
  onFiltersChange,
  grid,
  loading,
  failure,
  failureView,
  counts,
  allCount,
  subline,
  query,
  selection,
  onSelectionChange,
  actions
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [reason, setReason] = useState(null);

  const clearSelection = useCallback(
    () => onSelectionChange([]),
    [onSelectionChange]
  );

  const page = usePhoneTenantPage({
    fetchData: grid.fetchData,
    onFetchError: grid.onFetchError,
    onRowsLoaded: clearSelection,
    query: search.debouncedValue,
    status: filters.status,
    allSorting: filters.allSorting,
    sortKey: filters.sortKey,
    refreshKey: grid.refreshKey
  });

  const {rows} = page;
  const selectedIds = new Set(selection.rows.map(row => row.id));
  const showRows = !failure && !loading && rows !== null && rows.length > 0;

  const summaryParts = [subline];
  if (counts && !failure) {
    summaryParts.push(t('activeCount', {count: counts.active}));
    summaryParts.push(t('deletedCount', {count: counts.deleted}));
  }

  const leaveSelectMode = () => {
    setSelectMode(false);
    setReason(null);
    onSelectionChange([]);
  };

  const toggleRow = row =>
    onSelectionChange(
      selectedIds.has(row.id)
        ? selection.rows.filter(candidate => candidate.id !== row.id)
        : [...selection.rows, row]
    );

  let body;
  if (failure) {
    body = (
      <>
        <FailureBanner phone onRetry={failureView.onRetry} />
        <FailurePanel phone {...failureView} />
      </>
    );
  } else if (loading || rows === null) {
    body = <SkeletonCards />;
  } else if (rows.length === 0) {
    body = query ? (
      <EmptySearchPanel
        phone
        query={query}
        count={allCount}
        onClear={search.clearSearch}
      />
    ) : (
      <NoResultsPanel phone />
    );
  } else {
    body = (
      <>
        {rows.map(row => (
          <TenantPhoneCard
            key={row.id}
            data={row}
            selectMode={selectMode}
            selected={selectedIds.has(row.id)}
            onToggle={() => {
              setReason(null);
              toggleRow(row);
            }}
            onOpenTenant={onOpenTenant}
          />
        ))}
        <PhonePager
          skip={page.skip}
          limit={page.limit}
          total={page.total}
          onPrevious={page.goPrevious}
          onNext={page.goNext}
        />
      </>
    );
  }

  return (
    <ImageBackground style={{flex: 1}} source={backgroundImage}>
      {selectMode ? (
        <SelectBar
          count={selection.count}
          onClose={leaveSelectMode}
          onSelectAll={() => {
            setReason(null);
            onSelectionChange(rows || []);
          }}
        />
      ) : (
        <>
          <AppBar
            title={t('title')}
            subline={subline}
            onOpenMenu={onOpenMenu}
          />
          <StickyBar
            searchValue={search.searchValue}
            onSearchChange={search.handleSearchChange}
            onClearSearch={search.clearSearch}
            filterCount={activeFilterCount(filters)}
            onOpenFilters={() => setSheetOpen(true)}
            summary={summaryParts.join(' · ')}
            canSelect={showRows}
            onSelect={() => setSelectMode(true)}
          />
        </>
      )}
      <ScrollView
        contentContainerStyle={{
          padding: mobileGutter,
          paddingBottom: selectMode ? 24 : 32
        }}
      >
        {body}
      </ScrollView>
      {selectMode ? (
        <ActionBar
          actions={actions}
          reason={reason}
          onDisabledPress={setReason}
        />
      ) : null}
      {sheetOpen ? (
        <PhoneFiltersSheet
          initial={filters}
          allCount={allCount}
          onClose={() => setSheetOpen(false)}
          onApply={next => {
            setSheetOpen(false);
            onFiltersChange(next);
          }}
        />
      ) : null}
    </ImageBackground>
  );
};

export default TenantPhone;
